import * as React from "react"
import { ImSpinner2 } from "react-icons/im"

import clsxm from "@/lib/clsxm"

import Icon from "@/components/common/Icon/Icon"

export enum ButtonVariant {
  primary,
  outline,
  light,
  flat,
}

export enum ButtonSize {
  small,
  medium,
  large,
}

export interface ButtonProps
  extends ButtonStylingProps,
    React.ComponentPropsWithRef<`button`> {
  startIcon?: React.ElementType
  endIcon?: React.ElementType
  children: React.ReactNode
  className?: string
  disabled?: boolean
}

export interface ButtonStylingProps {
  buttonSize?: keyof typeof ButtonSize
  variant?: keyof typeof ButtonVariant
  isRounded?: boolean
  fullwidth?: boolean
  isLoading?: boolean
  className?: string
}

export const buttonStyling = ({
  buttonSize,
  variant,
  fullwidth,
  isRounded,
  isLoading,
  className,
}: ButtonStylingProps) => {
  return clsxm(
    `inline-flex items-center rounded-lg px-4 py-2 font-semibold`,
    `focus:outline-none focus-visible:ring focus-visible:ring-primary-500`,
    `shadow-sm`,
    `transition-colors duration-150`,
    [
      buttonSize === `large` && [`px-4 py-2.5`],
      buttonSize === `medium` && [`px-5 py-2`],
      buttonSize === `small` && [`px-2 py-1.5`],
    ],
    //#region  //*=========== Variants ===========
    [
      variant === `primary` && [
        `bg-primary-500 text-white`,
        `border border-primary-600`,
        `hover:bg-primary-600 hover:text-white`,
        `active:bg-primary-500`,
        `disabled:bg-primary-400 disabled:hover:bg-primary-400`,
      ],
      variant === `outline` && [
        `text-primary-500`,
        `border border-primary-500`,
        `bg-transparent`,
        `hover:bg-primary-500 hover:text-white active:bg-primary-100 disabled:bg-primary-100`,
      ],
      variant === `flat` && [
        `text-gray-900`,
        `border border-gray-500`,
        `bg-gray-100`,
        `hover:bg-gray-200 active:bg-gray-200 disabled:bg-gray-300`,
        `active:bg-gray-200`,
        `disabled:bg-gray-200`,
      ],
      variant === `light` && [
        `bg-white text-dark `,
        `border border-gray-300`,
        `hover:bg-gray-100 hover:text-dark`,
        `active:bg-white/80 disabled:bg-gray-200`,
      ],
    ],
    //#endregion  //*======== Variants ===========
    `disabled:cursor-not-allowed`,
    fullwidth && `w-full justify-center`,
    isRounded && `rounded-full`,
    isLoading &&
      `relative text-transparent transition-none hover:text-transparent disabled:cursor-wait`,
    className
  )
}

export const ButtonLoading = ({
  variant,
}: {
  variant: keyof typeof ButtonVariant
}) => {
  return (
    <div
      className={clsxm(
        `absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2`,
        {
          "text-white": [`primary`, `dark`].includes(variant),
          "text-black": [`light`].includes(variant),
          "text-primary-500": [`outline`, `ghost`].includes(variant),
        }
      )}
    >
      <ImSpinner2 className={`animate-spin`} />
    </div>
  )
}

const Button = ({
  buttonSize = `medium`,
  children,
  className,
  disabled: buttonDisabled,
  endIcon,
  fullwidth,
  isLoading,
  isRounded,
  startIcon,
  variant = `primary`,
  ...props
}: ButtonProps) => {
  const disabled = isLoading || buttonDisabled

  return (
    <button
      disabled={disabled}
      className={buttonStyling({
        buttonSize,
        variant,
        fullwidth,
        isRounded,
        isLoading,
        className,
      })}
      {...props}
    >
      {startIcon && <Icon className={`mr-1.5`} icon={startIcon} />}
      {isLoading && <ButtonLoading variant={variant} />}
      {children}
      {endIcon && <Icon className={`ml-1.5`} icon={endIcon} />}
    </button>
  )
}

export default Button
